'use client';

import pluralize from 'pluralize';
import { memo, type ReactNode } from 'react';

import { CheckIcon, InfoIcon, TriangleAlertIcon, XIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';
import { cn } from '@air/utils-tailwind';

import { type DeliveryState } from '~/stores/deliveryStore';

export type UploadStatusBannerProps = {
  deliveryState: DeliveryState;
  onDismiss: () => void;
};

export const UploadStatusBanner = memo(({ deliveryState, onDismiss }: UploadStatusBannerProps) => {
  if (deliveryState.status === 'idle') {
    return null;
  }

  const isLoading = deliveryState.status === 'loading';
  const isSuccess = deliveryState.status === 'success';

  let message: string;

  if (isLoading) {
    message = 'Upload in progress. Please stay on this screen.';
  } else if (isSuccess) {
    message = 'Upload successful, redirecting to product page';
  } else {
    const failedCount = deliveryState.errors?.length ?? 0;
    const fileTooLargeCount = deliveryState.errors?.filter((e) => e.reason === 'file_too_large')?.length ?? 0;

    if (fileTooLargeCount > 0 && fileTooLargeCount === failedCount) {
      message = `${pluralize('item', failedCount, true)} ${failedCount === 1 ? 'exceeds' : 'exceed'} Shopify's size or dimension limits`;
    } else if (failedCount > 0) {
      message = `Upload failed — ${pluralize('item', failedCount, true)} could not be uploaded`;
    } else {
      message = 'Upload failed — please try again';
    }
  }

  let statusIcon: ReactNode;

  if (isLoading) {
    statusIcon = <InfoIcon className="text-gray-12" size="md" />;
  } else if (isSuccess) {
    statusIcon = <CheckIcon className="text-gray-12" size="md" />;
  } else {
    statusIcon = <TriangleAlertIcon className="text-gray-12" size="md" />;
  }

  // <output> has implicit role="status" aria-live="polite" — intentional for screen-reader announcements
  return (
    <output
      aria-live={!isLoading && !isSuccess ? 'assertive' : 'polite'}
      className={cn(
        'flex shrink-0 items-center gap-3 px-4 py-3 font-medium text-14',
        isLoading && 'bg-altitude-6 text-altitude-12',
        isSuccess && 'bg-grass-9 text-gray-12',
        !isLoading && !isSuccess && 'bg-flash-9 text-gray-12',
      )}
    >
      {statusIcon}
      <span className="flex-1">{message}</span>
      {!isLoading && (
        <IconButton
          appearance="ghost"
          className="opacity-70 hover:bg-transparent hover:opacity-100 active:bg-transparent"
          color="gray"
          label="Dismiss"
          onClick={onDismiss}
          size="small"
        >
          <XIcon size="md" />
        </IconButton>
      )}
    </output>
  );
});

UploadStatusBanner.displayName = 'UploadStatusBanner';
